import type { DrawingLine } from '../model';
import type { DrawingAtom } from './graphDrawing';
import { distanceBetween, normalizedToPixel, type PixelPoint } from './bonds';
import type { PaperSize } from './geometry';
import { distanceToSegment } from '../rendering/chemicalGeometry';

export const CLICK_MOVEMENT_TOLERANCE_PX = 3;
export const MOUSE_SELECTION_DISTANCE_PX = 10;
export const TOUCH_SELECTION_DISTANCE_PX = 22;

export function usesTouchTarget(pointerType: string): boolean {
  return pointerType === 'touch' || pointerType === 'pen';
}

// #578: lasso selection's containment test. Standard ray-casting (even-odd rule): a point is
// inside when a ray cast from it crosses the polygon's edges an odd number of times. Works
// unchanged in either pixel or normalized space -- scaling x and y independently by the same
// factor for every point (polygon vertices and the test point alike) never changes which side of
// an edge a point falls on, so callers don't need to convert to pixels first (the rectangular
// marquee's own AABB check makes the same assumption).
export function pointInPolygon(
  point: { readonly x: number; readonly y: number },
  polygon: readonly { readonly x: number; readonly y: number }[]
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const vertexI = polygon[i]!;
    const vertexJ = polygon[j]!;
    const crossesRay =
      vertexI.y > point.y !== vertexJ.y > point.y &&
      point.x < ((vertexJ.x - vertexI.x) * (point.y - vertexI.y)) / (vertexJ.y - vertexI.y) + vertexI.x;
    if (crossesRay) inside = !inside;
  }
  return inside;
}

export function uniqueEndpoints(
  lines: readonly DrawingLine[],
  atoms: readonly DrawingAtom[],
  size: PaperSize
): readonly PixelPoint[] {
  const endpoints = [
    ...lines.flatMap((line) => [normalizedToPixel(line.start, size), normalizedToPixel(line.end, size)]),
    ...atoms.map((atom) => normalizedToPixel(atom.position, size))
  ];
  return endpoints.filter(
    (point, index) => endpoints.findIndex((candidate) => distanceBetween(point, candidate) < 0.5) === index
  );
}

export function nearestPoint(
  pointer: PixelPoint,
  points: readonly PixelPoint[],
  maximumDistance: number
): PixelPoint | undefined {
  return points.reduce<PixelPoint | undefined>((nearest, point) => {
    const distance = distanceBetween(pointer, point);
    if (distance > maximumDistance) return nearest;
    return !nearest || distance < distanceBetween(pointer, nearest) ? point : nearest;
  }, undefined);
}

export function selectionAtPoint(
  pointer: PixelPoint,
  atoms: readonly DrawingAtom[],
  lines: readonly DrawingLine[],
  size: PaperSize,
  maximumDistance: number
): { readonly kind: 'atom' | 'bond'; readonly id: string } | null {
  const nearestAtom = atoms.reduce<DrawingAtom | undefined>((nearest, atom) => {
    const distance = distanceBetween(pointer, normalizedToPixel(atom.position, size));
    if (distance > maximumDistance) return nearest;
    if (!nearest) return atom;
    const nearestDistance = distanceBetween(pointer, normalizedToPixel(nearest.position, size));
    return distance < nearestDistance ? atom : nearest;
  }, undefined);
  if (nearestAtom) return { kind: 'atom', id: nearestAtom.id };

  const nearestBond = lines.reduce<{ readonly id: string; readonly distance: number } | null>((nearest, line) => {
    const distance = distanceToSegment(pointer, normalizedToPixel(line.start, size), normalizedToPixel(line.end, size));
    return distance <= maximumDistance && (!nearest || distance < nearest.distance)
      ? { id: line.id, distance }
      : nearest;
  }, null);
  return nearestBond ? { kind: 'bond', id: nearestBond.id } : null;
}
